import { FC, memo } from "react";
import { useLocation } from "react-router-dom";

import Grid from "@mui/material/Grid";

import {
  IModelOption,
  allModelsOptions,
} from "../../../../common/components/select/ServiceSelect/ModelsToRunMultiselect";
import { useSyncShadow } from "../../../model-manager/hooks/useSyncShadow";
import { ServiceTypeEnum } from "../../../model-manager/variables/modelManager";
import { IDeviceTableRow, IModel } from "../../hooks/useDeviceTableRows";
import { useServiceActions } from "../../hooks/useServiceActions";
import { deviceDataVariable } from "../../variables/devices";
import DeviceModelItem from "./DeviceModelItem";

interface IDeviceModelProps {
  device: IDeviceTableRow;
  handleDialogOpen: () => void;
}

const DeviceModelsComponent: FC<IDeviceModelProps> = ({
  device,
  handleDialogOpen,
}): JSX.Element => {
  const { syncShadowsForNode } = useSyncShadow();

  const location = useLocation();
  const deviceModels = device?.models ?? [];

  location.state = { deviceId: device.rowId };

  const { modelsToRun } = device;

  return (
    <Grid gap={2} container spacing={4}>
      {modelsToRun.map((value: string): JSX.Element => {
        const model = allModelsOptions.find(
          (option): boolean => option.value === value
        );

        const mappedDevices = deviceModels.map(deviceModel => {
          if (deviceModel.serviceName === ServiceTypeEnum.GAS_LEAK) {
            deviceModel.serviceName = ServiceTypeEnum.LEAK;
          }

          if (deviceModel.serviceName === ServiceTypeEnum.SAFETY_AND_SECURITY) {
            deviceModel.serviceName = ServiceTypeEnum.PERSONNEL_DETECTION;
          }

          return deviceModel;
        });

        const deviceModel = mappedDevices.find(
          (deviceModel): boolean => deviceModel.serviceName === model?.name
        ) as IModel;

        const { changeModelStatusAction, deleteModelAction, serviceLoading } =
          useServiceActions({
            deviceModel,
            device,
          });

        const onChangeModelStatusHandler = (
          newStatus: boolean
        ): Promise<void> => {
          return changeModelStatusAction(!newStatus).then((): void => {
            syncShadowsForNode(device.node?.id);
          });
        };

        const onDeleteModelHandler = async (): Promise<void> => {
          const deletedModelId = await deleteModelAction();

          if (!deletedModelId) return;

          const filteredModels = deviceModels.filter(
            (model): boolean => model.serviceId !== deletedModelId
          );

          deviceDataVariable({ ...device, models: filteredModels });

          syncShadowsForNode(device.node?.id);
        };

        if (!model) return <></>;

        const { updateStatusLoading, deleteModelLoading } = serviceLoading;

        const runningLeakModel = deviceModels.find(
          m =>
            m.isRunning &&
            (m.serviceName === ServiceTypeEnum.LEAK ||
              m.serviceName === ServiceTypeEnum.GAS_LEAK)
        );

        const runningLiquidLeakModel = deviceModels.find(
          m => m.isRunning && m.serviceName === ServiceTypeEnum.LIQUID_LEAK
        );

        let runningLeakModels: string[] = [];

        if (runningLeakModel) {
          runningLeakModels = [runningLeakModel.serviceName];
        }

        if (runningLiquidLeakModel) {
          runningLeakModels = [runningLiquidLeakModel.serviceName];
        }

        if (runningLeakModel && runningLiquidLeakModel) {
          runningLeakModels = [
            runningLeakModel.serviceName,
            runningLiquidLeakModel.serviceName,
          ];
        }

        return (
          <Grid key={value}>
            <DeviceModelItem
              handleDialogOpen={handleDialogOpen}
              deviceId={device.rowId}
              updateStatusLoading={updateStatusLoading}
              deleteModelLoading={deleteModelLoading}
              deviceWithPanTilt={device.deviceData.hasPanTilt}
              model={model as IModelOption}
              currentModel={deviceModel as IModel}
              onStatusChange={onChangeModelStatusHandler}
              onDeleteModel={onDeleteModelHandler}
              runningLeakModels={runningLeakModels}
            />
          </Grid>
        );
      })}
    </Grid>
  );
};

const DeviceModels = memo(DeviceModelsComponent);

export default DeviceModels;
